import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Upload, FileText, Download, CreditCard, Truck,
  Users as UsersIcon, LogOut, KeyRound, ChevronDown, ClipboardCheck,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { useAuth } from "../context/AuthContext";
import { Logo } from "./Logo";
import { api } from "../lib/api";
import { toast } from "sonner";

const NAV = [
  { to: "/", label: "Overview", icon: LayoutDashboard, testid: "nav-overview-link", end: true },
  { to: "/upload", label: "Upload", icon: Upload, testid: "nav-upload-link", writer: true },
  { to: "/documents", label: "Documents", icon: FileText, testid: "nav-documents-link" },
  { to: "/review", label: "Review", icon: ClipboardCheck, testid: "nav-review-link" },
  { to: "/payments", label: "Payments", icon: CreditCard, testid: "nav-payments-link" },
  { to: "/fleet", label: "Fleet & Rental", icon: Truck, testid: "nav-fleet-link" },
  { to: "/exports", label: "Exports", icon: Download, testid: "nav-exports-link" },
  { to: "/users", label: "Users", icon: UsersIcon, testid: "nav-users-link", admin: true },
];

const ROLE_LABEL = { admin: "Admin", member: "Member", viewer: "Viewer" };

export const Layout = ({ children }) => {
  const navigate = useNavigate();
  const { user, isWriter, isAdmin, logout } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);

  const nav = NAV.filter((n) => (n.writer ? isWriter : true) && (n.admin ? isAdmin : true));
  const initials = (user?.name || user?.email || "?").trim().slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] text-foreground">
      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden lg:flex w-[260px] flex-col border-r border-border bg-card min-h-screen sticky top-0">
          <div className="flex items-center px-6 h-16 border-b border-border">
            <Logo size="md" />
          </div>
          <nav className="flex-1 px-3 py-4 space-y-1">
            {nav.map(({ to, label, icon: Icon, testid, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
                    isActive
                      ? "bg-accent text-accent-foreground border-l-2 border-primary pl-[10px]"
                      : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                  }`
                }
              >
                <Icon className="h-[18px] w-[18px]" />
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="px-4 py-4 border-t border-border">
            <div className="text-xs text-muted-foreground">Powered by Gemini Vision</div>
          </div>
        </aside>

        {/* Main */}
        <div className="flex-1 min-w-0 flex flex-col">
          <header className="sticky top-0 z-20 h-16 border-b border-border bg-card/95 backdrop-blur flex items-center justify-between px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 lg:hidden">
              <Logo size="sm" />
            </div>
            <div className="hidden lg:block text-sm text-muted-foreground">
              Extract &amp; organize toll-bill data from scanned PDFs
            </div>
            <div className="flex items-center gap-2">
              {isWriter && (
                <Button data-testid="topbar-upload-button" onClick={() => navigate("/upload")} className="gap-2">
                  <Upload className="h-4 w-4" /> Upload PDFs
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2 px-2" data-testid="user-menu-trigger">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-foreground">{initials}</span>
                    <span className="hidden sm:flex flex-col items-start leading-tight">
                      <span className="text-sm font-medium max-w-[140px] truncate">{user?.name || user?.email}</span>
                      <span className="text-[11px] text-muted-foreground">{ROLE_LABEL[user?.role] || user?.role}</span>
                    </span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="text-sm font-medium truncate">{user?.name || "Account"}</div>
                    <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setPwOpen(true)} data-testid="menu-change-password">
                    <KeyRound className="h-4 w-4 mr-2" /> Change password
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate("/users")} data-testid="menu-manage-users">
                      <UsersIcon className="h-4 w-4 mr-2" /> Manage users
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-red-600 focus:text-red-600">
                    <LogOut className="h-4 w-4 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>
          {/* Mobile nav */}
          <div className="lg:hidden flex gap-1 overflow-x-auto border-b border-border bg-card px-2 py-2">
            {nav.map(({ to, label, icon: Icon, testid, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={`m-${testid}`}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium ${
                    isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground"
                  }`
                }
              >
                <Icon className="h-4 w-4" /> {label}
              </NavLink>
            ))}
          </div>
          <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 max-w-[1400px] w-full mx-auto">{children}</main>
        </div>
      </div>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
};

const ChangePasswordDialog = ({ open, onOpenChange }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (next.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      await api.changePassword(current, next);
      toast.success("Password updated");
      setCurrent(""); setNext("");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle>Change password</DialogTitle>
          <DialogDescription>Enter your current password and a new one.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label htmlFor="cp-current">Current password</Label>
            <Input id="cp-current" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="cp-current" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cp-new">New password</Label>
            <Input id="cp-new" type="password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="cp-new" placeholder="At least 6 characters" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="cp-save">{saving ? "Saving..." : "Update password"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
